import { CubeIcon } from '@radix-ui/react-icons';
import { ChevronDown, FileCodeIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { CopyTextButton } from '@eluve/blocks';
import { TooltipLabel } from '@eluve/blocks';
import {
  Box,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Divider,
  Label,
  legacyTextStyles,
} from '@eluve/components';
import { latestHumanSOAPNoteWithPromptTemplateFragment } from '@eluve/frontend-appointment-hooks';

interface LatestAppointmentTemplateProps {
  appointmentId: string;
  handleModelInfoUpdate?: (info: string) => void;
}

const LatestAppointmentTemplateContent: React.FC<
  LatestAppointmentTemplateProps
> = ({ appointmentId, handleModelInfoUpdate }) => {
  const appointment = useCompleteFragment({
    fragment: latestHumanSOAPNoteWithPromptTemplateFragment,
    key: {
      id: appointmentId,
    },
    strict: false,
  });

  const output = appointment?.humanOutputs[0]?.output?.llm_output;

  const promptTemplate = output?.metadata?.prompt_template;
  const promptVariant = output?.metadata?.prompt_variant;
  const outputTemplate = output?.metadata?.output_template;
  const outputVariant = output?.metadata?.output_variant;

  useEffect(() => {
    if (!promptTemplate || !output) return;
    const { args, id, modelType } = output.modelPromptTemplate.model_args;

    handleModelInfoUpdate?.(
      JSON.stringify(
        {
          modelArgs: {
            id,
            modelType,
            ...args,
          },
          llmOutputTemplate: {
            id: outputTemplate?.id,
            name: outputTemplate?.name,
          },
          promptTemplate: {
            id: promptTemplate?.id,
            name: promptTemplate?.name,
          },
        },
        null,
        2,
      ),
    );
  }, [output, handleModelInfoUpdate, promptTemplate, outputTemplate]);

  if (!output) {
    return (
      <CollapsibleContent>
        <div className="flex w-full gap-3 rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-sm text-text-warning-subtle">
          <TriangleAlertIcon className="size-5" />
          No summary generated for this appointment.
        </div>
      </CollapsibleContent>
    );
  }

  const modelArgs = output.modelPromptTemplate.model_args;

  return (
    <CollapsibleContent>
      <div className="flex flex-col gap-0 rounded-lg border border-border-quaternary text-sm text-text-secondary">
        <div className="flex flex-1 items-center gap-2 p-2">
          <TooltipLabel label="Prompt Template">
            <FileCodeIcon className="mx-1 size-6" />
          </TooltipLabel>
          <Link
            to={`/admin/prompts/${promptTemplate?.id}/prompt/variant/${promptVariant?.variantNumber}`}
          >
            {promptTemplate?.name} / Variant: {promptVariant?.variantNumber}
          </Link>
        </div>
        {outputTemplate && (
          <div className="flex flex-1 items-center gap-2 p-2">
            <TooltipLabel label="Output Template">
              <FileCodeIcon className="mx-1 size-6" />
            </TooltipLabel>
            <Link
              to={`/admin/output-templates/${outputTemplate?.id}/variant/${outputVariant?.variantNumber}`}
            >
              {outputTemplate?.name} / Variant: {outputVariant?.variantNumber}
            </Link>
          </div>
        )}
        {modelArgs && (
          <>
            <Divider className="ml-12" />
            <div className="flex flex-1 items-start gap-2 p-2 ">
              <TooltipLabel label="Model">
                <CubeIcon className="mx-1 size-6" />
              </TooltipLabel>
              <div className="flex flex-col space-y-1">
                <p>{modelArgs.modelType}</p>
                <div>
                  Temperature: <b>{modelArgs.args?.temperature ?? 'null'}</b>
                </div>
                <div>
                  Version: <b>{modelArgs.args?.model ?? 'null'}</b>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </CollapsibleContent>
  );
};

export const LatestAppointmentTemplate: React.FC<
  LatestAppointmentTemplateProps
> = ({ appointmentId }) => {
  const [modalInfo, setModalInfo] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  return (
    <Collapsible open={opened} onOpenChange={setOpened}>
      <div className="space-y-2">
        <Box hStack>
          <Label className="px-1">Latest Summary Info</Label>
          <CopyTextButton
            disabled={!modalInfo}
            copyText={modalInfo ?? ''}
            hideContentInTooltip
          />
          <CollapsibleTrigger>
            <Box hStack>
              <ChevronDown
                className='size-5 transition-transform duration-300 ease-in-out data-[state="open"]:-rotate-180'
                data-state={opened ? 'open' : 'closed'}
              />
              <p className={legacyTextStyles.body({ size: 'm' })}>
                {!opened ? 'Show' : 'Hide'}
              </p>
            </Box>
          </CollapsibleTrigger>
        </Box>
        <LatestAppointmentTemplateContent
          appointmentId={appointmentId}
          handleModelInfoUpdate={setModalInfo}
        />
      </div>
    </Collapsible>
  );
};
